// REACT
import React from 'react';

// SERVICES
import './CulturesMapTab.style.scss';
import { getMainCultureByYear } from './MapCommon';

// Interface
import { IParcelSimu } from '../Interface/Simulation/IParcelSimu';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import { IResultInfo } from '../Interface/Simulation/IResultInfo';
import { IParcelCulture } from '../Interface/Api/Response/Exploitation/Parcels/IParcelCulture';
import { ORGANIC_STATES } from 'utils/GlobalVariables';
import dayjs from 'dayjs';
import { getParcelOrganicStateAtYear } from 'utils/tools_functions';
import { ICampaign } from '../Interface/Exploitation/Parcels/ICampaign';
import {
  getCultureCampaignKey,
  getCultureCampaignName,
} from 'utils/Datagrid/CustomParcelstDataGridUtils';

type ParcelInfoParams = {
  cultureData: IParcelCulture[] | string;
  parcelInfo: IParcelSimu;
  parcels: IResultInfo[];
  forecastCampaign?: ICampaign;
  minYear?: number | null;
  from?: string | null;
};

const ParcelInfo = ({
  cultureData,
  parcelInfo,
  parcels,
  forecastCampaign = { startYear: 0, endYear: 0, stringFormat: '' },
  minYear = null,
  from = null,
}: ParcelInfoParams): React.JSX.Element => {
  const parentParcelName = parcels?.find((parcel) => parcel?.id === parcelInfo?.parent)?.name;

  const isSplittedParcel = parcelInfo?.cultures?.length > 1;

  const parcel = parcels?.find((parcel: IResultInfo) => parcel?.id === parcelInfo?.id);

  const currentCultures =
    typeof cultureData === 'string'
      ? cultureData
      : cultureData?.map((culture) => culture?.name).join(' & ');

  let isMinYear: boolean = false;
  if (forecastCampaign?.startYear > 0 && minYear !== null) {
    isMinYear = forecastCampaign.startYear <= minYear;
  }

  let currentOrganicState = 'unknown';
  if (from === 'mapParcels') {
    if (
      parcelInfo?.organicState === ORGANIC_STATES.organic ||
      parcelInfo?.organicState === ORGANIC_STATES.conventional
    ) {
      currentOrganicState = parcelInfo?.organicState;
    } else {
      if (parcelInfo?.conversionDate && forecastCampaign.startYear) {
        const currentCampaign = dayjs().year(forecastCampaign.startYear).month(7).date(1);
        const isBeforeCurrentCampain = dayjs(parcelInfo?.conversionDate).isBefore(currentCampaign);

        currentOrganicState = isBeforeCurrentCampain
          ? ORGANIC_STATES.conversion
          : ORGANIC_STATES.conventional;
      }
    }
  }
  if (from === 'mapResult' && parcel) {
    currentOrganicState = getParcelOrganicStateAtYear(parcel, forecastCampaign.startYear);
  }

  return (
    <div id="popup-content">
      <div className="parcel_title">
        <h1>
          {parcelInfo?.name}{' '}
          {isSplittedParcel && (
            <>
              <ContentCutIcon />
            </>
          )}
        </h1>
      </div>
      <div className="popup-content__wrapper">
        <div className="popup-content__left">
          {from === 'mapResult' && !isSplittedParcel && (
            <p>
              <span className="popup-content__left__label">Culture :</span>
              <span className="popup-content__left__parcel">{currentCultures}</span>
            </p>
          )}
          <p>
            <span className="popup-content__left__label">
              {parcelInfo?.groupSurface ? 'Surface totale' : 'Surface'} :
            </span>
            <span className="popup-content__left__parcel">
              {parcelInfo?.groupSurface
                ? Number(parcelInfo?.groupSurface.toFixed(2))
                : Number(parcelInfo?.surface.toFixed(2))}
            </span>
          </p>
          <p>
            <span className="popup-content__left__label">
              {from === 'mapResult'
                ? 'Précédent'
                : `${forecastCampaign.startYear - 1} - ${forecastCampaign.endYear - 1}`}{' '}
              :
            </span>
            <span className="popup-content__left__parcel">
              {from === 'mapResult'
                ? parcelInfo.previousCulture1.name
                : getCultureCampaignName(parcelInfo.parcelCultures, 1, forecastCampaign)}
            </span>
          </p>
          {!isMinYear && (
            <p>
              <span className="popup-content__left__label">
                {from === 'mapResult'
                  ? 'Anté-précédent'
                  : `${forecastCampaign.startYear - 2} - ${forecastCampaign.endYear - 2}`}{' '}
                :
              </span>
              <span className="popup-content__left__parcel">
                {from === 'mapResult'
                  ? parcelInfo.previousCulture2.name
                  : getCultureCampaignName(parcelInfo.parcelCultures, 2, forecastCampaign)}
              </span>
            </p>
          )}
          <p>
            <span className="popup-content__left__label">Type de sol : </span>
            <span className="popup-content__left__parcel">{parcelInfo?.groundType?.name}</span>
          </p>
          <p>
            <span className="popup-content__left__label">Irrigabilité :</span>
            <span className="popup-content__left__parcel">
              {parcelInfo?.isIrrigableOnly || parcelInfo?.isIrrigable ? 'Oui' : 'Non'}
            </span>
          </p>
          {(from === 'mapResult' || from === 'mapParcels') && (
            <p>
              <span className="popup-content__left__label">Parcelle :</span>
              <span className="popup-content__left__parcel">
                {currentOrganicState === ORGANIC_STATES.conversion
                  ? 'Conversion'
                  : currentOrganicState === ORGANIC_STATES.organic
                  ? 'Bio'
                  : 'Conventionnelle'}
              </span>
            </p>
          )}
        </div>
        {isSplittedParcel && (
          <div className="popup-content__right">
            {parcelInfo?.cultures.map((culture, index) => (
              <div key={index}>
                <h4 className="popup-content__right__title">Parcelle {index + 1}</h4>
                <p>
                  <span className="popup-content__right__label">Culture :</span>
                  <span className="popup-content__right__culture">{culture?.name}</span>
                </p>
                <p>
                  <span className="popup-content__right__label">Surface :</span>
                  <span className="popup-content__right__culture">
                    {Number(culture?.surface.toFixed(2))}
                  </span>
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
      {parcelInfo?.parent && (
        <p>
          {parentParcelName ? (
            <span>
              <i>Cette parcelle est regroupée avec {parentParcelName}</i>
            </span>
          ) : (
            <span>
              <i>Cette parcelle fait partie d'un regroupement</i>
            </span>
          )}
        </p>
      )}
      {parcelInfo?.isParent && (
        <p>
          <span>
            <i>Cette parcelle est parente d'un regroupement</i>
          </span>
        </p>
      )}
    </div>
  );
};

export default ParcelInfo;
